import {
  ManageSupplier,
  ManageSupplierStatus,
  ManageSupplierType,
  SupplierInheritance,
} from 'src/types/manage-suppliers/ManageSupplier';
import { SupplierFilterItem } from 'src/types/manage-suppliers/SupplierFilter';
import {
  PO_TRANSMIT_EMAIL,
  PO_TRANSMIT_INACTIVE,
  PO_TRANSMIT_OFFLINE,
  SUPPLIER_STATUS_ACTIVE,
  SUPPLIER_STATUS_ARCHIVED,
  SUPPLIER_STATUS_INACTIVE,
  SUPPLIER_VALIDATION_PENDING,
  SUPPLIER_VALIDATION_VALIDATED,
  UPSERT_VALIDATION_FAILURE,
} from './supplierFilterItemMappers';

const str = (value: string | null | undefined): string => value ?? '';

export const toSupplierType = (item: SupplierFilterItem): ManageSupplierType => {
  if (item.isPunchoutCatalog) return 'punchout';
  if (item.isOfflineSupplier) return 'offline';
  return 'hosted';
};

/** Display/filter values for the Order Type grid column. */
export const TRANSMISSION_TYPE_STATUS_LABEL = {
  onlineCatalog: 'Online Catalog',
  onlinePunchout: 'Online Punchout',
  offlineEmail: 'Offline Email',
  pendingValidation: 'Pending Validation',
  validationFailure: 'Validation Failure',
  offlineLogOnly: 'Offline Log Only',
  inactive: 'Inactive',
  archived: 'Archived',
} as const;

/** Backend online `ClientIsDeleted` / `CatalogIsDeleted`: 0 = active, 1 = archived, 2 = inactive */
const ONLINE_CLIENT_ACTIVE = 0;
const ONLINE_CLIENT_ARCHIVED = 1;
const ONLINE_CLIENT_INACTIVE = 2;

const getStatusFlags = (item: SupplierFilterItem) => ({
  isOfflineSupplier: Boolean(item.isOfflineSupplier),
  offlineIsActive: Number(item.offlineIsActive),
  clientIsDeleted: Number(item.clientIsDeleted),
  catalogIsDeleted: Number(item.catalogIsDeleted),
});

const isOnlineStatus = (
  clientIsDeleted: number,
  catalogIsDeleted: number,
  status: number
): boolean => clientIsDeleted === status || catalogIsDeleted === status;

/**
 * Same rule as backend ShowActive:
 * (IsOfflineSupplier && OfflineIsActive == 1) ||
 * (!IsOfflineSupplier && ClientIsDeleted == 0 && CatalogIsDeleted == 0)
 */
export const isActiveSupplierItem = (item: SupplierFilterItem): boolean => {
  const { isOfflineSupplier, offlineIsActive, clientIsDeleted, catalogIsDeleted } =
    getStatusFlags(item);

  return (
    (isOfflineSupplier && offlineIsActive === SUPPLIER_STATUS_ACTIVE) ||
    (!isOfflineSupplier &&
      clientIsDeleted === ONLINE_CLIENT_ACTIVE &&
      catalogIsDeleted === ONLINE_CLIENT_ACTIVE)
  );
};

/**
 * Same rule as backend ShowInactive, plus catalog-level inactive for Show All:
 * (IsOfflineSupplier && OfflineIsActive == 0) ||
 * (!IsOfflineSupplier && (ClientIsDeleted == 2 || CatalogIsDeleted == 2))
 */
export const isInactiveSupplierItem = (item: SupplierFilterItem): boolean => {
  const { isOfflineSupplier, offlineIsActive, clientIsDeleted, catalogIsDeleted } =
    getStatusFlags(item);

  return (
    (isOfflineSupplier && offlineIsActive === SUPPLIER_STATUS_INACTIVE) ||
    (!isOfflineSupplier &&
      isOnlineStatus(clientIsDeleted, catalogIsDeleted, ONLINE_CLIENT_INACTIVE))
  );
};

/**
 * Same rule as backend ShowArchived, plus catalog-level archived for Show All:
 * (IsOfflineSupplier && OfflineIsActive == 2) ||
 * (!IsOfflineSupplier && (ClientIsDeleted == 1 || CatalogIsDeleted == 1))
 *
 * ShowActive already requires CatalogIsDeleted == 0. Without this, Show All keeps
 * live "Online Catalog" / "Online Punchout" labels on red archived-catalog rows.
 */
export const isArchivedSupplierItem = (item: SupplierFilterItem): boolean => {
  const { isOfflineSupplier, offlineIsActive, clientIsDeleted, catalogIsDeleted } =
    getStatusFlags(item);

  return (
    (isOfflineSupplier && offlineIsActive === SUPPLIER_STATUS_ARCHIVED) ||
    (!isOfflineSupplier &&
      isOnlineStatus(clientIsDeleted, catalogIsDeleted, ONLINE_CLIENT_ARCHIVED))
  );
};

// Offline email validationStatus -> label when poTransmitMethod is email (1).
const toEmailTransmissionLabel = (validationStatus: number): string => {
  switch (validationStatus) {
    case SUPPLIER_VALIDATION_VALIDATED:
      return TRANSMISSION_TYPE_STATUS_LABEL.offlineEmail;
    case SUPPLIER_VALIDATION_PENDING:
      return TRANSMISSION_TYPE_STATUS_LABEL.pendingValidation;
    case UPSERT_VALIDATION_FAILURE:
      return TRANSMISSION_TYPE_STATUS_LABEL.validationFailure;
    default:
      return '';
  }
};

// Order Type label shown in the Manage Suppliers grid column.
export const toTransmissionTypeLabel = (item: SupplierFilterItem): string => {
  if (isArchivedSupplierItem(item)) {
    return TRANSMISSION_TYPE_STATUS_LABEL.archived;
  }

  if (isInactiveSupplierItem(item)) {
    return TRANSMISSION_TYPE_STATUS_LABEL.inactive;
  }

  if (!item.isOfflineSupplier) {
    return item.isPunchoutCatalog
      ? TRANSMISSION_TYPE_STATUS_LABEL.onlinePunchout
      : TRANSMISSION_TYPE_STATUS_LABEL.onlineCatalog;
  }

  if (item.poTransmitMethod === PO_TRANSMIT_EMAIL) {
    return toEmailTransmissionLabel(item.validationStatus);
  }

  switch (item.poTransmitMethod) {
    case PO_TRANSMIT_OFFLINE:
      return TRANSMISSION_TYPE_STATUS_LABEL.offlineLogOnly;
    case PO_TRANSMIT_INACTIVE:
      return TRANSMISSION_TYPE_STATUS_LABEL.inactive;
    default:
      return '';
  }
};

export const toTransmissionType = toTransmissionTypeLabel;

export const toStatus = (item: SupplierFilterItem): ManageSupplierStatus => {
  if (isArchivedSupplierItem(item)) {
    return 'archived';
  }

  if (isInactiveSupplierItem(item)) {
    return 'inactive';
  }

  if (isActiveSupplierItem(item)) {
    return 'active';
  }

  return 'inactive';
};

export const toActionType = (item: SupplierFilterItem): ManageSupplier['actionType'] => {
  if (item.clientIsDeleted || item.catalogIsDeleted) {
    return null;
  }

  if (item.isPunchoutCatalog) {
    return 'openPunchout';
  }

  if (item.isOfflineSupplier && item.isActive) {
    return 'createPo';
  }

  if (item.isOfflineSupplier && !item.isActive) {
    return null;
  }

  if (!item.isPunchoutCatalog && !item.isOfflineSupplier) {
    return 'openCatalog';
  }

  return null;
};

const resolveSupplierId = (item: SupplierFilterItem): number => {
  if (item.isOfflineSupplier && item.offlineSupplierId != null) {
    return item.offlineSupplierId;
  }

  return item.supplierClientIdCombined;
};

export const toSupplierInheritance = (value?: string | null): SupplierInheritance | null => {
  if (value === 'Inherited' || value === 'NotInherited') {
    return value;
  }

  return null;
};

/** `true` = unlinked (UnAttach), `false` = linked (Attach), `undefined` = hide icon. */
export const toShowUnlinkedIndicator = (
  supplierInheritance?: string | null
): boolean | undefined => {
  if (supplierInheritance === 'NotInherited') {
    return true;
  }

  if (supplierInheritance === 'Inherited') {
    return false;
  }

  return undefined;
};

export const mapSupplier = (item: SupplierFilterItem, index: number): ManageSupplier => {
  const supplierType = toSupplierType(item);
  const status = toStatus(item);

  return {
    rowId: `${String(item.supplierClientIdCombined)}-${index}`,
    supplierId: resolveSupplierId(item),
    supplierClientId: item.supplierClientId,
    supplierClientIdCombined: item.supplierClientIdCombined,
    supplierCompanyId: item.supplierCompanyId ?? 0,
    propertyCompanyId: item.propertyCompanyId ?? 0,
    supplierCompanyName: str(item.supplierCompanyName),
    offlineSupplierId: item.offlineSupplierId,
    supplierName: str(item.supplierCompanyName),
    catalog: str(item.catalogName),
    catalogId: item.catalogId,
    transmissionTypeStatus: toTransmissionTypeLabel(item),
    createdBy: str(item.createdBy),
    contactName: str(item.contact),
    address: [item.addressLine1, item.addressLine2].filter(Boolean).join(', '),
    addressLine1: str(item.addressLine1),
    addressLine2: str(item.addressLine2),
    apId: str(item.apId),
    externalSupplierName: str(item.externalSupplierName),
    externalSupplierNumber: str(item.externalSupplierNumber),
    managementApId: str(item.managementApId),
    managementExternalSupplierName: str(item.managementExternalSupplierName),
    propertyName: str(item.propertyName),
    xtrnlSupplierInherited: item.xtrnlSupplierInherited ?? null,
    supplierInheritance: toSupplierInheritance(item.supplierInheritance),
    accountNumber: str(item.accountNumber),
    phone: str(item.phoneNumber),
    email: str(item.email),
    city: str(item.city),
    stateProvince: str(item.state),
    zipPostalCode: str(item.zipCode),
    country: str(item.country),
    programInfo: str(item.programInfo),
    supplierType,
    status,
    isMapped: item.isMapped,
    isOfflineSupplier: item.isOfflineSupplier,
    isSharedOfflineSupplier: item.isSharedOfflineSupplier,
    poTransmitMethod: item.poTransmitMethod,
    validationStatus: item.validationStatus,
    isValidation: item.isValidation,
    validationStatusDate: item.validationStatusDate,
    validationErrorMessage: item.validationErrorMessage,
    actionType: toActionType(item),
    canEdit: true,
  };
};
